import { z } from 'zod';
import { ROUTE_STATUSES } from '../constants/index.js';
import { dateOnlySchema, decimalStringSchema, uuidSchema } from './common.js';
/**
 * The reporting contract.
 *
 * Every figure here is **derived at read time** from the documents, the ledger,
 * the stops and the samples — there is no summary table and no nightly job. The
 * reason is the one that already keeps the customer balance derived (D72): a
 * stored aggregate can disagree with the rows meant to explain it, and when a
 * document is cancelled three weeks later the aggregate is the thing nobody
 * remembers to recompute.
 *
 * Money stays a string, as everywhere else, so nothing is rounded by JSON.
 */
/** Every report but the receivables takes the same window, both ends inclusive. */
export const reportRangeQuerySchema = z.object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
/** The window the server actually used, after filling in the defaults. */
export const reportRangeSchema = z.object({
    from: dateOnlySchema,
    to: dateOnlySchema,
});
/**
 * Sales and returns are reported apart, not as one net figure.
 *
 * A month of 100k sold with 20k returned and a month of 80k sold with nothing
 * returned both net to 80k, and they are not the same month. `net` is the
 * difference, so a reader does not have to subtract.
 */
export const reportTotalsSchema = z.object({
    salesNet: decimalStringSchema,
    returnsNet: decimalStringSchema,
    /** `salesNet - returnsNet`, VAT excluded. */
    net: decimalStringSchema,
    vat: decimalStringSchema,
    gross: decimalStringSchema,
});
// --- the dashboard ----------------------------------------------------------
/** One bar of the dashboard chart. Days with no activity are present as zeros. */
export const reportDaySchema = z.object({
    day: dateOnlySchema,
    salesNet: decimalStringSchema,
    returnsNet: decimalStringSchema,
    collected: decimalStringSchema,
    documentCount: z.number().int(),
});
export const reportCountsSchema = z.object({
    documents: z.number().int(),
    /** Cancelled documents, counted but excluded from every money figure. */
    cancelledDocuments: z.number().int(),
    /** Customers with at least one active document in the window. */
    customersServed: z.number().int(),
    payments: z.number().int(),
});
export const reportRouteSummarySchema = z.object({
    total: z.number().int(),
    completed: z.number().int(),
    started: z.number().int(),
    stopsPlanned: z.number().int(),
    stopsVisited: z.number().int(),
    stopsSkipped: z.number().int(),
    /** Summed from the planned geometry: the plan's distance, not the driven one. */
    plannedDistanceMeters: z.number().int(),
});
/**
 * What is owed, as it stands **now** rather than at the end of the window.
 *
 * A receivable is a present fact: what a customer owed on 31 March is a
 * different question, and answering it from this figure would be wrong.
 */
export const reportReceivablesSummarySchema = z.object({
    total: decimalStringSchema,
    /** Customers whose balance is above zero. */
    debtorCount: z.number().int(),
    /** Of `total`, the part sitting on documents issued more than 30 days ago. */
    overdueTotal: decimalStringSchema,
});
export const dashboardTopProductSchema = z.object({
    productId: uuidSchema,
    sku: z.string(),
    name: z.string(),
    quantity: decimalStringSchema,
    net: decimalStringSchema,
});
export const dashboardTopCustomerSchema = z.object({
    customerId: uuidSchema,
    code: z.string(),
    businessName: z.string(),
    net: decimalStringSchema,
    collected: decimalStringSchema,
});
export const dashboardSchema = z.object({
    range: reportRangeSchema,
    totals: reportTotalsSchema,
    /** Money received in the window, whatever it was received against. */
    collected: decimalStringSchema,
    counts: reportCountsSchema,
    routes: reportRouteSummarySchema,
    receivables: reportReceivablesSummarySchema,
    days: z.array(reportDaySchema),
    topProducts: z.array(dashboardTopProductSchema),
    topCustomers: z.array(dashboardTopCustomerSchema),
});
// --- the reports ------------------------------------------------------------
/**
 * A report comes back whole, not paginated, and says so when it was cut short.
 *
 * Aggregates are read to be compared and totalled, which a page of twenty rows
 * does not allow, and counting a grouped set costs a second query for a figure
 * the rows already carry. The cap exists so a mistaken window cannot build an
 * unbounded response; `truncated` travels in the payload because a silently
 * short report is worse than a refused one.
 */
export const REPORT_MAX_ROWS = 5000;
export const salesByProductRowSchema = z.object({
    productId: uuidSchema,
    sku: z.string(),
    name: z.string(),
    categoryName: z.string().nullable(),
    unit: z.string(),
    soldQuantity: decimalStringSchema,
    returnedQuantity: decimalStringSchema,
    /** Sold minus returned, which is what actually left the vans. */
    netQuantity: decimalStringSchema,
    salesNet: decimalStringSchema,
    returnsNet: decimalStringSchema,
    net: decimalStringSchema,
    documentCount: z.number().int(),
});
export const salesByProductQuerySchema = reportRangeQuerySchema.extend({
    categoryId: uuidSchema.optional(),
});
export const salesByCustomerRowSchema = z.object({
    customerId: uuidSchema,
    code: z.string(),
    businessName: z.string(),
    comune: z.string().nullable(),
    documentCount: z.number().int(),
    salesNet: decimalStringSchema,
    returnsNet: decimalStringSchema,
    net: decimalStringSchema,
    gross: decimalStringSchema,
    /** Collected **in the window**, which need not relate to the window's documents. */
    collected: decimalStringSchema,
    /** The account balance as it stands now, for context. */
    balance: decimalStringSchema,
});
export const AGING_BUCKETS = ['current', 'days31to60', 'days61to90', 'over90'];
/** The upper edge of each bucket in days since issue; the last one is open-ended. */
export const AGING_BUCKET_DAYS = {
    current: 30,
    days31to60: 60,
    days61to90: 90,
    over90: null,
};
/**
 * What is unpaid, and for how long.
 *
 * The buckets are built from the **documents** still short of their gross, aged
 * by issue date. `balance` beside them is the ledger's figure, and the two can
 * legitimately differ — a return or an adjustment moves the balance without
 * belonging to any unpaid document. Showing both is deliberate: one reconciled-
 * looking number would hide the discrepancy rather than explain it.
 */
export const receivablesRowSchema = z.object({
    customerId: uuidSchema,
    code: z.string(),
    businessName: z.string(),
    phone: z.string().nullable(),
    balance: decimalStringSchema,
    unpaidTotal: decimalStringSchema,
    buckets: z.record(z.enum(AGING_BUCKETS), decimalStringSchema),
    oldestUnpaidOn: dateOnlySchema.nullable(),
    lastPaymentOn: dateOnlySchema.nullable(),
});
/** Receivables are a present fact, so this report takes no window. */
export const receivablesQuerySchema = z.object({
    /** Hide customers whose balance is zero or in credit. Default true. */
    debtorsOnly: z.enum(['true', 'false']).optional(),
});
export const routePerformanceRowSchema = z.object({
    routeId: uuidSchema,
    routeDate: dateOnlySchema,
    name: z.string().nullable(),
    status: z.enum(ROUTE_STATUSES),
    driverName: z.string().nullable(),
    vehiclePlate: z.string().nullable(),
    stops: z.number().int(),
    visited: z.number().int(),
    skipped: z.number().int(),
    plannedDistanceMeters: z.number().int().nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    /** Minutes between the two ends; null until the route has both. */
    durationMinutes: z.number().int().nullable(),
    documentCount: z.number().int(),
    net: decimalStringSchema,
    collected: decimalStringSchema,
});
export const routePerformanceQuerySchema = reportRangeQuerySchema.extend({
    userId: uuidSchema.optional(),
    status: z.enum(ROUTE_STATUSES).optional(),
});
//# sourceMappingURL=report.js.map